// react
import { useState } from 'react';

// types
import { Feedback } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Alert, AlertTitle, Box, Collapse, Grid, Typography } from '@mui/material';

// icons
import { ExpandLess as IconExpandLess, ExpandMore as IconExpandMore } from '@mui/icons-material';

// props
export interface AlertFeedbackProps {
	feedback?: Feedback;
	items?: Feedback[];
}

export const AlertFeedback = ({ feedback, items = [] }: AlertFeedbackProps) => {
	// hooks
	const { t } = useTranslation();

	// state
	const [isExpandedIndex, setIsExpandedIndex] = useState<number | null>(null);

	let feedbackItems = feedback ? [feedback] : [];
	if (items.length) {
		feedbackItems = items;
	}

	if (!feedbackItems.length) {
		return null;
	}

	return (
		<Grid container spacing={0.5}>
			{feedbackItems.map((item, index) => {
				const { alert, errors, response, title } = item;
				const { children, ...alertProps } = alert;
				return (
					<Grid container key={`${index}-${alertProps.severity}`}>
						<Grid item xs={12}>
							<Alert sx={{ alignItems: 'flex-start' }} {...alertProps}>
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'column',
										gap: 0.5,
									}}>
									{title && <AlertTitle {...title} />}
									{children}
									{errors && errors.length > 0 && (
										<Box sx={{ mt: 1 }}>
											<Typography variant="subtitle2">{t('errors')}</Typography>
											<Box
												component="ul"
												sx={{
													mt: 0.5,
													mb: 0,
													pl: 2,
												}}>
												{errors.map((error, i) => (
													<li key={`${error}-${i}`}>
														<Typography variant="body2">{error}</Typography>
													</li>
												))}
											</Box>
										</Box>
									)}
									{response && (
										<Box
											component="button"
											onClick={(e) => {
												e.preventDefault();
												e.stopPropagation();
												setIsExpandedIndex((prev) => (prev === index ? null : index));
											}}
											sx={{
												alignItems: 'center',
												border: 'none',
												background: 'none',
												color: 'inherit',
												cursor: 'pointer',
												display: 'flex',
												gap: 0.5,
												p: 0,
											}}>
											<Typography sx={{ color: 'inherit' }} variant="body2">
												{t('seeDetails')}
											</Typography>
											{isExpandedIndex === index ? <IconExpandLess /> : <IconExpandMore />}
										</Box>
									)}
								</Box>
							</Alert>
						</Grid>
						{response && (
							<Grid item xs={12}>
								<Collapse in={isExpandedIndex === index} sx={{ mt: 2 }}>
									<Box
										sx={(theme) => ({
											backgroundColor: theme.palette.brand.grey[100],
											borderStyle: `1px solid ${theme.palette.divider}`,
											borderRadius: 2,
											maxHeight: 300,
											overflow: 'auto',
											p: 2,
											whiteSpace: 'pre-wrap',
											wordBreak: 'break-word',
										})}>
										<Typography component="pre" sx={{ fontFamily: 'monospace', m: 0 }}>
											{JSON.stringify(response, null, 2)}
										</Typography>
									</Box>
								</Collapse>
							</Grid>
						)}
					</Grid>
				);
			})}
		</Grid>
	);
};
